/**
 * The one sticky Add to cart bar (F11 / G1, Curtis 2026-10-19), shared by every product page:
 * LureShield (sections/ra-ls1-buybox.liquid), Reel Armour singles and 4 packs (ra-reel-buybox), the
 * old Complete Solution kit (ra-kit-buybox) and the Horizon pages (WrapStrap, Pro XL, CS4, the
 * LureShield packs: snippets/ra-sbar-horizon.liquid). The Rod Sleeve box keeps its own copy of the
 * same rule (assets/ra-nb2-min.js, B2), which this one was taken from. Look: assets/ra-pdp-shared.css.
 *
 * The rule, at every width (phones and desktop): the bar comes up only once the page's own Add to cart
 * has scrolled up off the TOP of the screen, and goes away as soon as it is back on screen. Never on
 * the first screen, never beside the main button, never while the cart drawer is open. An
 * IntersectionObserver says when the button crosses the edge; every scroll and resize re-measures
 * too, once a frame (a jump such as End or a status-bar tap can carry the button past the edge with
 * no observer call).
 *
 *   window.raSbar(bar, {
 *     watch: () => [elements],   the page's own Add to cart button(s); hidden ones are skipped
 *     hold: () => bool,          optional: keep the bar as it is (e.g. while a field is typed in)
 *     block: () => bool,         optional: keep the bar down (e.g. a sheet is open)
 *     onToggle: (on) => {},      optional
 *   }) -> { sync(), on }
 *
 * window.raSbarHorizon(bar) drives the Horizon pages' bar: its line follows the chosen variant and
 * the − 1 + count, and its button presses the page's own Add to cart (the same form, the same
 * /cart/add, the same one-add-at-a-time guard in blocks/ra-pdp-title.liquid).
 */
(() => {
  if (window.raSbar) return;
  const html = document.documentElement;

  const cartOpen = () => {
    const b = document.body;
    const rc = document.getElementById('rebuy-cart');
    return (
      html.classList.contains('rebuy-cart-visible') ||
      !!(b && b.classList.contains('rebuy-cart-visible')) ||
      !!(rc && (rc.getAttribute('aria-hidden') === 'false' || rc.classList.contains('is-visible')))
    );
  };

  window.raSbar = (bar, o) => {
    if (!bar) return null;
    o = o || {};
    const list = () => (typeof o.watch === 'function' ? o.watch() : o.watch || []).filter(Boolean);
    const api = { on: false, sync: () => kick() };
    let raf = 0;

    /* past: one of the buttons is wholly above the top edge; seen: one is on screen. A button that is
       not drawn (display: none, a sold-out state) counts as neither. */
    const measure = () => {
      const h = window.innerHeight || html.clientHeight;
      let past = false;
      let seen = false;
      list().forEach((el) => {
        const r = el.getBoundingClientRect();
        if (!r.height && !r.width) return;
        if (r.bottom <= 0) past = true;
        else if (r.top < h) seen = true;
      });
      return past && !seen;
    };

    const set = (on) => {
      api.on = on;
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      bar.querySelectorAll('button, a[href]').forEach((b) => {
        b.tabIndex = on ? 0 : -1;
      });
      /* a class on <html>: Tab and anchor jumps keep the focused item above the bar (ra-pdp-shared.css) */
      html.classList.toggle('ra-sbar-on', on);
      if (on) html.style.setProperty('--ra-sbar-h', `${bar.offsetHeight}px`);
      if (o.onToggle) o.onToggle(on);
    };

    const sync = () => {
      raf = 0;
      if (o.hold && o.hold()) return;
      const on = measure() && !cartOpen() && !(o.block && o.block());
      if (on !== api.on) set(on);
    };
    const kick = () => {
      if (!raf) raf = requestAnimationFrame(sync);
    };

    try {
      if (typeof window.IntersectionObserver === 'function') {
        const io = new window.IntersectionObserver(kick);
        list().forEach((el) => io.observe(el));
      }
    } catch {
      /* the scroll / resize check below still runs */
    }
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick, { passive: true });
    if ('MutationObserver' in window) {
      const mo = new MutationObserver(kick);
      mo.observe(html, { attributes: true, attributeFilter: ['class'] });
      if (document.body) mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
    html.classList.add('ra-sbar-page');
    bar.setAttribute('aria-hidden', 'true');
    bar.querySelectorAll('button, a[href]').forEach((b) => {
      b.tabIndex = -1;
    });
    sync();
    return api;
  };

  /* ---------- a failed add (code review 2026-10-23 P2, fixed 2026-10-26) ----------
     The bar is only up once the page's own Add to cart, and the error line beside it, are off the top of the screen,
     so a failed add from the bar changed nothing the shopper could see. As the Rod Sleeve bar (assets/ra-nb2-min.js):
     "Couldn't add to cart" over a short reason in red (.ra-sbar.is-err, ra-pdp-shared.css). The page's own role=alert
     still speaks to screen readers.
       window.raSbarErr(bar, error)   error as assets/ra-pdp-cart.js rejects it (.uncertain, .offline, .status)
       window.raSbarErr(bar)          back to the bar's own lines
     "Try again" on the button only when nothing was added; when the add may have landed (uncertain, a 422 that added
     what was in stock) the reason says "Check your cart" and the button keeps its label. */
  window.raSbarErr = (bar, error) => {
    if (!bar) return;
    const info = bar.querySelector('.ra-sbar__info:not([data-sbar-err])');
    const cta = bar.querySelector('.ra-sbar__cta');
    let box = bar.querySelector('[data-sbar-err]');
    if (error === undefined) {
      if (!bar.classList.contains('is-err')) return;
      bar.classList.remove('is-err');
      if (box) box.style.display = 'none';
      if (info) info.style.display = '';
      if (cta && cta.dataset.sbarLabel != null) {
        cta.innerHTML = cta.dataset.sbarLabel;
        delete cta.dataset.sbarLabel;
      }
      return;
    }
    const e = error || {};
    const why = e.uncertain || e.status === 422
      ? { short: 'Check your cart' }
      : e.offline
        ? { short: "You're offline", again: true }
        : e.status === 429
          ? { short: 'Wait, then try again', again: true }
          : { short: 'Please try again', again: true };
    if (!box) {
      box = document.createElement('p');
      box.className = 'ra-sbar__info';
      box.setAttribute('data-sbar-err', '');
      box.innerHTML = '<span class="ra-sbar__l1"><span class="ra-sbar__name"></span></span><span class="ra-sbar__l2"></span>';
      box.querySelector('.ra-sbar__name').textContent = "Couldn't add to cart";
      if (info) info.after(box);
      else bar.querySelector('.ra-sbar__in')?.prepend(box);
    }
    box.querySelector('.ra-sbar__l2').textContent = why.short;
    box.style.display = '';
    if (info) info.style.display = 'none';
    bar.classList.add('is-err');
    if (cta && why.again) {
      if (cta.dataset.sbarLabel == null) cta.dataset.sbarLabel = cta.innerHTML;
      cta.textContent = 'Try again';
    }
  };

  /* ---------- the Horizon pages (snippets/ra-sbar-horizon.liquid) ---------- */
  window.raSbarHorizon = (bar) => {
    if (!bar || bar.dataset.raSbarInit) return;
    bar.dataset.raSbarInit = '1';
    let cfg = {};
    try {
      cfg = JSON.parse(bar.querySelector('[data-sbar-config]').textContent);
    } catch {
      return;
    }
    const section = bar.closest('.shopify-section') || document;
    const nameEl = bar.querySelector('[data-sbar-name]');
    const priceEl = bar.querySelector('[data-sbar-price]');
    const varEl = bar.querySelector('[data-sbar-var]');
    const cta = bar.querySelector('[data-sbar-btn]');
    const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: cfg.currency || 'USD' });
    const form = () => section.querySelector(`product-form-component[data-product-id="${cfg.product}"]`) || section.querySelector('product-form-component');
    const main = () => {
      const f = form();
      return f && f.querySelector('.add-to-cart-button, [ref="addToCartButton"]');
    };
    const txt = (el, t) => {
      if (el && el.textContent !== t) el.textContent = t;
    };

    const paint = () => {
      const f = form();
      const idEl = f && f.querySelector('input[name="id"], select[name="id"]');
      const qEl = f && f.querySelector('input[name="quantity"]');
      const v = (idEl && cfg.variants[String(idEl.value)]) || cfg.variants[String(cfg.current)] || null;
      const q = Math.max(1, parseInt(qEl && qEl.value, 10) || 1);
      /* K5: the short name beside the total; the size, then the count when more than one ("5–6 ft · 2 packs") */
      txt(nameEl, cfg.name);
      txt(priceEl, v ? fmt.format((v.price * q) / 100) : '');
      txt(varEl, [v && cfg.showVariant ? v.title : '', q > 1 ? `${q} ${cfg.many || 'items'}` : ''].filter(Boolean).join(' · '));
      const ok = !!(v && v.available);
      if (cta) {
        cta.disabled = !ok;
        txt(cta, ok ? cfg.atc : cfg.soldOut);
      }
    };

    let pr = 0;
    const repaint = () => {
      if (!pr) pr = requestAnimationFrame(() => {
        pr = 0;
        paint();
      });
    };
    /* the variant picker and − 1 + change the form's id and quantity; Horizon swaps parts of the
       form after a variant change, so look again a moment later too */
    ['change', 'input', 'click'].forEach((t) => section.addEventListener(t, () => {
      repaint();
      setTimeout(repaint, 400);
    }));
    document.addEventListener('shopify:cart:lines-update', repaint);
    /* review (2026-10-19): Horizon writes the form's variant id from code only once its variant fetch
       is done (assets/product-form.js, after `await event.promise`), which fires no change event; on a
       slow connection that is long after the 400 ms look. Repaint when that same fetch settles
       (StandardEvents.productSelect, as assets/local-pickup.js listens for; product-form's own handler
       awaited it first, so the id is written by then). Backstops: every scroll repaints (only text
       that differs is touched) and so does the bar coming up, so the bar never shows one variant while
       its button adds another. */
    section.addEventListener('shopify:product:select', (e) => {
      repaint();
      const p = e && e.promise;
      if (p && typeof p.then === 'function') {
        p.then(() => requestAnimationFrame(repaint), () => {});
      }
    });
    window.addEventListener('scroll', repaint, { passive: true });

    if (cta) {
      cta.addEventListener('click', () => {
        const b = main();
        if (!b) return;
        if (b.disabled) {
          b.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          return;
        }
        /* the page's own button adds (same form, same /cart/add); "puppet" skips its fly-to-cart
           animation from a button that is off screen (as Horizon's own sticky bar did) */
        window.raSbarErr(bar);
        b.dataset.puppet = 'true';
        try {
          b.click();
        } finally {
          b.dataset.puppet = 'false';
        }
      });
    }

    /* a failed add (code review 2026-10-23 P2, fixed 2026-10-26): product-form.js writes the reason into its error line
       beside the page's own button (ref addToCartTextError) and hides it again 10 s later; the bar follows that line.
       Its "could not confirm" (no answer, a 429) may have landed; a refusal (422) may have added part of it. */
    const errLine = () => {
      const f = form();
      return f && f.querySelector('[ref="addToCartTextError"]');
    };
    const follow = () => {
      const el = errLine();
      if (!el || el.classList.contains('hidden')) window.raSbarErr(bar);
      else window.raSbarErr(bar, /could not confirm/i.test(el.textContent || '') ? { uncertain: true } : { status: 422 });
    };
    const watched = form();
    if (watched && 'MutationObserver' in window) {
      new MutationObserver(follow).observe(watched, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
    }

    paint();
    window.raSbar(bar, { watch: () => [main()], onToggle: (on) => on && paint() });
  };
})();
